// src/components/mascot/Elli.js
// ─────────────────────────────────────────────────────────────
// Elli — la mascotte Ellipse. Un blob en forme d'ellipse (clin d'œil au nom
// de la marque), couleurs de marque (vert #bfd56c, marine #1a1a2e, jaune
// #f5d377 — voir design.md). Pur SVG, pas d'asset externe : léger, et
// facile à décliner (taille, humeur) sans dépendre d'un fichier image.
//
// Règle "mignon" : grands yeux (ratio œil/corps élevé = mignon, principe
// de base du design de personnage), un reflet qui donne de la brillance,
// petits bras arrondis pour l'expressivité, jamais de ligne dure.
// ─────────────────────────────────────────────────────────────

import React from "react";

const KEYFRAMES = `
@keyframes elli-breathe {
  0%, 100% { transform: scale(1) translateY(0); }
  50% { transform: scale(1.02, 0.98) translateY(-2px); }
}
@keyframes elli-blink {
  0%, 92%, 100% { transform: scaleY(1); }
  95% { transform: scaleY(0.12); }
}
@keyframes elli-spark {
  0%, 100% { opacity: 0.65; transform: scale(0.9) rotate(0deg); }
  50% { opacity: 1; transform: scale(1.2) rotate(8deg); }
}
@keyframes elli-wave {
  0%, 100% { transform: rotate(0deg); }
  50% { transform: rotate(-18deg); }
}
`;

// `talking` : étire légèrement la bouche (variante utilisée quand une
// réponse est en train de s'afficher) — sans ça, le popover de chat a un
// personnage figé pendant qu'il "répond", ce qui casse l'illusion.
//
// `animated` : le bouton flottant reste monté et visible sur TOUTE page,
// tout le temps où le membre est connecté — 3 animations CSS infinies
// tournant en permanence pour ça, peu importe si la bulle est ouverte,
// était un coût gratuit (répété sur chaque page de toute la session).
// Statique par défaut ; `animated=true` seulement pendant que la bulle de
// chat est réellement ouverte (voir ElliWidget.js).
// `theme` : null | "halloween" | "christmas" — un seul accessoire de tête
// à la fois (voir seasonal.js pour les fenêtres de dates).
function Elli({ size = 56, talking = false, mood = "happy", animated = true, theme = null }) {
  const mouthPath = talking
    ? "M 41 66 Q 50 76 59 66 Q 50 72 41 66"   // bouche ouverte, "en train de parler"
    : mood === "thinking"
      ? "M 43 67 Q 50 67 57 67"                 // ligne neutre
      : "M 38 63 Q 50 75 62 63 Q 50 70 38 63";  // sourire plein, bouche ouverte joyeuse

  const anim = (name, duration) => (animated ? `${name} ${duration} ease-in-out infinite` : "none");

  return (
    <>
      {animated && <style>{KEYFRAMES}</style>}
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        style={{ animation: anim("elli-breathe", "3.2s"), display: "block", overflow: "visible" }}
      >
        <defs>
          <radialGradient id="elli-body" cx="38%" cy="30%" r="75%">
            <stop offset="0%" stopColor="#e3f0ab" />
            <stop offset="55%" stopColor="#bfd56c" />
            <stop offset="100%" stopColor="#96b34a" />
          </radialGradient>
        </defs>

        {/* Ombre douce au sol */}
        <ellipse cx="50" cy="94" rx="20" ry="3.5" fill="#0f172a" opacity="0.1" />

        {/* Bras — petits nubs arrondis, un peu de vie sans surcharger */}
        <circle cx="15" cy="62" r="7" fill="#bfd56c" stroke="#1a1a2e" strokeWidth="2" style={{ animation: anim("elli-wave", "2.4s"), transformOrigin: "15px 56px" }} />
        <circle cx="85" cy="64" r="7" fill="#bfd56c" stroke="#1a1a2e" strokeWidth="2" />

        {/* Antenne — le signal "intelligence", étoile plutôt qu'un simple point.
            Remplacée par un chapeau saisonnier quand `theme` est actif (un
            seul accessoire à la fois sur la tête, pas de surcharge). */}
        {theme === "halloween" ? (
          <g>
            <ellipse cx="50" cy="21" rx="17" ry="4" fill="#2d1b4e" stroke="#1a1a2e" strokeWidth="1.5" />
            <path d="M 39 21 Q 50 -10 58 -11 Q 52 -2 61 21 Z" fill="#3d2566" stroke="#1a1a2e" strokeWidth="1.5" strokeLinejoin="round" />
            <rect x="39" y="15" width="22" height="5" rx="2" fill="#f5d377" />
          </g>
        ) : theme === "christmas" ? (
          <g>
            <ellipse cx="50" cy="21" rx="17" ry="4" fill="#ffffff" stroke="#1a1a2e" strokeWidth="1.5" />
            <path d="M 39 21 Q 48 -6 62 -9 Q 56 3 61 21 Z" fill="#d93a3a" stroke="#1a1a2e" strokeWidth="1.5" strokeLinejoin="round" />
            <circle cx="62" cy="-9" r="5" fill="#ffffff" stroke="#1a1a2e" strokeWidth="1.5" />
          </g>
        ) : (
          <>
            <line x1="50" y1="15" x2="50" y2="5" stroke="#1a1a2e" strokeWidth="2.5" strokeLinecap="round" />
            <g style={{ animation: anim("elli-spark", "1.8s"), transformOrigin: "50px 4px" }}>
              <path d="M 50 -1 L 52 2 L 55 4 L 52 6 L 50 9 L 48 6 L 45 4 L 48 2 Z" fill="#f5d377" />
            </g>
          </>
        )}

        {/* Corps — ellipse, pas un cercle : c'est la signature. Reflet en
            haut-gauche pour donner du volume (glossy, pas plat). */}
        <ellipse cx="50" cy="56" rx="39" ry="35" fill="url(#elli-body)" stroke="#1a1a2e" strokeWidth="2.5" />
        <ellipse cx="34" cy="38" rx="11" ry="7" fill="#ffffff" opacity="0.4" transform="rotate(-25 34 38)" />

        {/* Joues — plus vives, plus rondes */}
        <ellipse cx="24" cy="62" rx="8" ry="5.5" fill="#f5a97f" opacity="0.5" />
        <ellipse cx="76" cy="62" rx="8" ry="5.5" fill="#f5a97f" opacity="0.5" />

        {/* Yeux — grands, ovales, iris + reflet double pour la brillance.
            Grand ratio œil/visage = le principe #1 du "cute" en design de
            personnage. */}
        <g style={{ animation: anim("elli-blink", "4.6s"), transformOrigin: "50px 52px" }}>
          <ellipse cx="36" cy="52" rx="8" ry="9.5" fill="#1a1a2e" />
          <ellipse cx="64" cy="52" rx="8" ry="9.5" fill="#1a1a2e" />
          <circle cx="38.5" cy="48" r="2.6" fill="#ffffff" />
          <circle cx="66.5" cy="48" r="2.6" fill="#ffffff" />
          <circle cx="34" cy="56" r="1.3" fill="#ffffff" opacity="0.7" />
          <circle cx="62" cy="56" r="1.3" fill="#ffffff" opacity="0.7" />
        </g>

        {/* Bouche */}
        <path d={mouthPath} stroke="#1a1a2e" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" fill={talking || mood !== "thinking" ? "#e8577a" : "none"} fillOpacity="0.85" />
      </svg>
    </>
  );
}

export default React.memo(Elli);
